import React, { useEffect } from 'react';

import { FocusShell } from '../components/FocusShell';
import { Spinner } from '../components/Spinner';
import { cognitoAuthConfig, cognitoDomain } from '../config/oidc-config';
import usePageTitle from '../hooks/usePageTitle';

/**
 * Where an invited person goes to choose their first password, and where
 * anyone who has forgotten theirs goes to replace it.
 *
 * Both are the same Cognito flow, and it lives in the hosted UI rather than
 * here: setting a password means proving control of the address, which the
 * hosted UI does by mailing a one-time code (through Dory's own sender, so it
 * arrives branded). This page exists because the invitation email needs ONE
 * stable link it can carry — `${portal}/set-password` — while the hosted-UI
 * URL is assembled from the client id, scopes and redirect that only the
 * portal's build knows. The API would otherwise need Cognito's client
 * configuration in its environment just to write an email.
 *
 * Rendered as a redirect rather than a link so the person lands on the form
 * itself, one click from their inbox.
 */
const buildForgotPasswordUrl = (): string => {
  const params = new URLSearchParams({
    client_id: cognitoAuthConfig.client_id,
    response_type: cognitoAuthConfig.response_type,
    scope: cognitoAuthConfig.scope,
    redirect_uri: cognitoAuthConfig.redirect_uri,
  });
  return `${cognitoDomain}/forgotPassword?${params.toString()}`;
};

const SetPassword: React.FC = () => {
  usePageTitle('Set your password');
  const target = buildForgotPasswordUrl();

  useEffect(() => {
    window.location.replace(target);
  }, [target]);

  return (
    <FocusShell title="Set your password">
      <div className="flex flex-col items-center gap-3 text-center">
        {/* role="status": a screen reader hears that the page is working. */}
        <Spinner size="lg" label="Taking you to the password form…" />
        {/* The redirect is immediate; this is for anyone it does not reach. */}
        <a className="text-sm font-medium text-primary underline underline-offset-4" href={target}>
          Continue to set your password
        </a>
      </div>
    </FocusShell>
  );
};

export default SetPassword;
